import { type SxProps, type Theme, Typography } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { FlagFilledIcon } from "components/icons";
import { BadgeBase, type BadgeProps } from "./BadgeBase";

interface PriorityProps extends BadgeProps {
  type: "high" | "medium" | "low" | "critical" | string;
  hideLabel?: boolean;
  flagOnly?: boolean;
  iconSx?: SxProps<Theme>;
  containerStyles?: SxProps<Theme>;
}
export const PriorityBadge = ({
  type,
  hideLabel = false,
  flagOnly = false,
  iconSx,
  containerStyles,
  ...rest
}: PriorityProps) => {
  const theme = useTheme();
  let backgroundColor: string | undefined;
  let backgroundMode: "light" | "dark";
  let textColor: string | undefined;
  switch (type) {
    case "high":
      backgroundColor = theme.palette.warning.tint;
      backgroundMode = "dark";
      textColor = theme.palette.warning.main;
      break;
    case "medium":
      backgroundColor = theme.palette["warning-minor"].tint;
      backgroundMode = "light";
      textColor = theme.palette["warning-minor"].main;
      break;
    case "low":
      backgroundColor = theme.palette.primitive.neutrals.neutral["100"];
      backgroundMode = "light";
      textColor = theme.palette.primitive.neutrals.neutral["700"];
      break;
    case "critical":
      backgroundColor = theme.palette.error.tint;
      backgroundMode = "dark";
      textColor = theme.palette.error.main;
      break;
    default:
      backgroundColor = theme.palette.neutral["015"];
      backgroundMode = "light";
      break;
  }
  if (flagOnly) {
    return (
      <FlagFilledIcon
        htmlColor={textColor}
        sx={{ fontSize: "16px", ...iconSx }}
      />
    );
  }

  return (
    <BadgeBase
      backgroundColor={backgroundColor}
      backgroundMode={backgroundMode}
      containerStyles={
        hideLabel
          ? {
              width: "24px",
              height: "24px",
              padding: "4px",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "4px",
              ...containerStyles,
            }
          : {
              width: "84px",
              padding: "4px 12px 4px 8px",
              justifyContent: "left",
              borderRadius: "4px",
              ...containerStyles,
            }
      }
      leadingIcon={
        <FlagFilledIcon fontSize="small" htmlColor={textColor} sx={iconSx} />
      }
      {...rest}
    >
      {!hideLabel && (
        <Typography
          variant="caption-xs"
          textTransform={"capitalize"}
          sx={{ color: `${theme.palette.lightBg.high} !important` }}
        >
          {type}
        </Typography>
      )}
    </BadgeBase>
  );
};
